import React, { useEffect, useState } from 'react';
import { CalendarIcon, SettingsIcon, ChevronDownIcon, ChevronUpIcon, ChevronRightIcon, InfoIcon, CreditCardIcon, WalletIcon, CreditCardIcon as CardIcon, ExternalLinkIcon } from 'lucide-react';
import { MetadataEditor, MetadataItem } from './MetadataEditor';
export type PaymentProvider = {
  id: string;
  name: string;
  icon: React.ReactNode;
};
interface CommonPaymentOptionsProps {
  metadata: MetadataItem[];
  redirectUrl: string;
  expiryDate: string;
  onMetadataChange: (metadata: MetadataItem[]) => void;
  onRedirectUrlChange: (url: string) => void;
  onExpiryDateChange: (date: string) => void;
  hideExpiry?: boolean;
  hideRedirect?: boolean;
  selectedPaymentProviders?: string[];
  onSelectedPaymentProvidersChange?: (providers: string[]) => void;
  storePaymentMethod?: boolean;
  onStorePaymentMethodChange?: (value: boolean) => void;
  passFeesToCustomer?: boolean;
  onPassFeesToCustomerChange?: (value: boolean) => void;
}
export function CommonPaymentOptions({
  metadata,
  redirectUrl,
  expiryDate,
  onMetadataChange,
  onRedirectUrlChange,
  onExpiryDateChange,
  hideExpiry = false,
  hideRedirect = false,
  selectedPaymentProviders = [],
  onSelectedPaymentProvidersChange,
  storePaymentMethod,
  onStorePaymentMethodChange,
  passFeesToCustomer: externalPassFees,
  onPassFeesToCustomerChange
}: CommonPaymentOptionsProps) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [optionsSectionExpanded, setOptionsSectionExpanded] = useState(false);
  const [expiryDays, setExpiryDays] = useState(30);
  const [showPaymentProviders, setShowPaymentProviders] = useState(false);
  const [internalPassFees, setInternalPassFees] = useState(false);
  const passFeesToCustomer = externalPassFees !== undefined ? externalPassFees : internalPassFees;
  const handlePassFeesChange = (checked: boolean) => {
    if (onPassFeesToCustomerChange) {
      onPassFeesToCustomerChange(checked);
    } else {
      setInternalPassFees(checked);
    }
  };
  const paymentProviders: PaymentProvider[] = [{
    id: 'card-payments',
    name: 'Card Payments',
    icon: <CreditCardIcon size={15} className="text-blue-600" data-id="element-1959" />
  }, {
    id: 'alipay',
    name: 'Alipay',
    icon: <WalletIcon size={15} className="text-blue-500" data-id="element-1960" />
  }, {
    id: 'wechatpay',
    name: 'WechatPay',
    icon: <WalletIcon size={15} className="text-green-500" data-id="element-1961" />
  }, {
    id: 'rabbit-card',
    name: 'Rabbit Card',
    icon: <CardIcon size={15} className="text-purple-500" data-id="element-1962" />
  }, {
    id: 'linepay',
    name: 'LinePay',
    icon: <WalletIcon size={15} className="text-green-600" data-id="element-1963" />
  }];
  const defaultExpiryDays = 30;
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
  useEffect(() => {
    if (expiryDate) {
      const [month, day, year] = expiryDate.split('/').map(p => parseInt(p, 10));
      const expiryDateObj = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setExpiryDays(Math.ceil((expiryDateObj.getTime() - today.getTime()) / (1000 * 3600 * 24)));
    }
  }, [expiryDate]);
  useEffect(() => {
    if (!expiryDate) {
      const date = new Date();
      date.setDate(date.getDate() + defaultExpiryDays);
      onExpiryDateChange(formatDate(date));
    }
  }, [expiryDate, onExpiryDateChange, defaultExpiryDays]);
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${month}/${day}/${date.getFullYear()}`;
  };
  const parseDate = (dateString: string): Date => {
    const [month, day, year] = dateString.split('/').map(p => parseInt(p, 10));
    return new Date(year, month - 1, day);
  };
  const generateCalendar = () => {
    const today = new Date();
    const currentMonth = showDatePicker ? expiryDate ? parseDate(expiryDate) : today : today;
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const weeks: (Date | null)[][] = [];
    let days: (Date | null)[] = [];
    for (let i = 0; i < startDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i));
      if (days.length === 7 || i === daysInMonth) {
        while (days.length < 7) days.push(null);
        weeks.push(days);
        days = [];
      }
    }
    return {
      month: currentMonth,
      weeks
    };
  };
  const calendar = generateCalendar();
  const selectedDate = expiryDate ? parseDate(expiryDate) : null;
  const handlePrevMonth = () => {
    calendar.month.setMonth(calendar.month.getMonth() - 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleNextMonth = () => {
    calendar.month.setMonth(calendar.month.getMonth() + 1);
    setShowDatePicker(false);
    setTimeout(() => setShowDatePicker(true), 0);
  };
  const handleDateSelect = (date: Date) => {
    onExpiryDateChange(formatDate(date));
    setShowDatePicker(false);
  };
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  const isSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() && date.getMonth() === selectedDate.getMonth() && date.getFullYear() === selectedDate.getFullYear();
  };
  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };
  const togglePaymentProvider = (providerId: string) => {
    if (!onSelectedPaymentProvidersChange) return;
    const updated = selectedPaymentProviders.includes(providerId) ? selectedPaymentProviders.filter(id => id !== providerId) : [...selectedPaymentProviders, providerId];
    onSelectedPaymentProvidersChange(updated);
  };
  const hasMetadata = metadata && metadata.length > 0;
  const hasRedirectUrl = redirectUrl && redirectUrl.length > 0;
  const hasExpiryDate = expiryDate && expiryDate.length > 0;
  const hasSelectedProviders = selectedPaymentProviders && selectedPaymentProviders.length > 0;
  const hasStorePayment = storePaymentMethod === true;
  const hasPassFees = passFeesToCustomer === true;
  const hasAnyData = hasMetadata || hasRedirectUrl || hasExpiryDate || hasSelectedProviders || hasStorePayment || hasPassFees;
  const quickDates = [{
    label: '7 days',
    days: 7
  }, {
    label: '30 days',
    days: 30
  }, {
    label: '90 days',
    days: 90
  }];
  return <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1964">
      <button className="w-full text-left flex items-center justify-between px-6 py-4" onClick={() => setOptionsSectionExpanded(!optionsSectionExpanded)} data-id="element-1965">
        <div className="flex items-center gap-2" data-id="element-1966">
          <SettingsIcon size={16} className="text-[#F7941D]" data-id="element-1967" />
          <span className="text-sm font-semibold text-gray-900" data-id="element-1968">
            Additional options
          </span>
          {!optionsSectionExpanded && hasAnyData && <span className="text-xs text-gray-500 font-normal ml-1" data-id="element-1969">
              —{' '}
              {[hasExpiryDate && !hideExpiry ? `Expires ${expiryDate}` : null, hasStorePayment ? 'Store card enabled' : null, hasPassFees ? 'Fees passed to customer' : null, hasSelectedProviders ? `${selectedPaymentProviders.length} methods` : null, hasRedirectUrl && !hideRedirect ? 'Redirect URL' : null, hasMetadata ? `${metadata.length} metadata` : null].filter(Boolean).join(', ')}
            </span>}
        </div>
        {optionsSectionExpanded ? <ChevronDownIcon size={16} className="text-gray-400" data-id="element-1970" /> : <ChevronRightIcon size={16} className="text-gray-400" data-id="element-1971" />}
      </button>

      {optionsSectionExpanded && <div data-id="element-1972">
          {/* ── Store Payment Method ── */}
          {onStorePaymentMethodChange !== undefined && <div className="border-t border-gray-200 px-6 py-5" data-id="element-1973">
              <div className="flex items-start justify-between" data-id="element-1974">
                <div data-id="element-1975">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-1976">
                    Store payment method
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-1977">
                    Customer authorizes saving their payment method for future
                    charges. Supported:{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500" data-id="element-1978">
                      Cards
                    </span>{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500" data-id="element-1979">
                      Alipay
                    </span>{' '}
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500" data-id="element-1980">
                      WechatPay
                    </span>
                  </p>
                </div>
                <label className="flex items-center cursor-pointer flex-shrink-0 mt-0.5 ml-4" data-id="element-1981">
                  <input type="checkbox" className="sr-only" checked={storePaymentMethod || false} onChange={e => onStorePaymentMethodChange(e.target.checked)} data-id="element-1982" />
                  <div className={`w-10 h-[22px] rounded-full transition-colors ${storePaymentMethod ? 'bg-[#F7941D]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-1983">
                    <div className={`w-[18px] h-[18px] rounded-full bg-white transform transition-transform shadow-sm ${storePaymentMethod ? 'translate-x-[18px]' : ''}`} data-id="element-1984" />
                  </div>
                </label>
              </div>
            </div>}

          {/* ── Pass Fees to Customer ── */}
          <div className="border-t border-gray-200 px-6 py-5" data-id="element-1985">
            <div className="flex items-start justify-between" data-id="element-1986">
              <div data-id="element-1987">
                <h4 className="text-sm font-medium text-gray-900" data-id="element-1988">
                  Pass fees to customer
                </h4>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-1989">
                  Transaction processing fees are added on top of the requested
                  amount. The customer pays the total including fees.
                </p>
              </div>
              <label className="flex items-center cursor-pointer flex-shrink-0 mt-0.5 ml-4" data-id="element-1990">
                <input type="checkbox" className="sr-only" checked={passFeesToCustomer} onChange={e => handlePassFeesChange(e.target.checked)} data-id="element-1991" />
                <div className={`w-10 h-[22px] rounded-full transition-colors ${passFeesToCustomer ? 'bg-[#F7941D]' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-1992">
                  <div className={`w-[18px] h-[18px] rounded-full bg-white transform transition-transform shadow-sm ${passFeesToCustomer ? 'translate-x-[18px]' : ''}`} data-id="element-1993" />
                </div>
              </label>
            </div>
          </div>

          {/* ── Link Expiration ── */}
          {!hideExpiry && <div className="border-t border-gray-200 px-6 py-5" data-id="element-1994">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-1995">
                Link expiration
              </h4>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-1996">
                Expires in {expiryDays} days. Default: {defaultExpiryDays} days
                — change in{' '}
                <span className="text-[#F7941D] cursor-pointer" data-id="element-1997">
                  payment settings
                </span>
                .
              </p>
              <div className="relative mt-3" data-id="element-1998">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1999">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-2000" />
                </div>
                <input type="text" className={`${inputClass} pl-9 cursor-pointer`} placeholder="MM/DD/YYYY" value={expiryDate} onClick={() => setShowDatePicker(!showDatePicker)} readOnly data-id="element-2001" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer" onClick={() => setShowDatePicker(!showDatePicker)} data-id="element-2002">
                  {showDatePicker ? <ChevronUpIcon size={14} className="text-gray-400" data-id="element-2003" /> : <ChevronDownIcon size={14} className="text-gray-400" data-id="element-2004" />}
                </div>

                {/* Date Picker — fixed width */}
                {showDatePicker && <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg p-4 w-80 left-0" data-id="element-2005">
                    <div className="flex justify-between items-center mb-3" data-id="element-2006">
                      <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handlePrevMonth} data-id="element-2007">
                        <ChevronDownIcon size={14} className="transform rotate-90" data-id="element-2008" />
                      </button>
                      <div className="text-sm font-semibold text-gray-900" data-id="element-2009">
                        {calendar.month.toLocaleString('default', {
                  month: 'long',
                  year: 'numeric'
                })}
                      </div>
                      <button type="button" className="p-1 hover:bg-gray-100 rounded-md" onClick={handleNextMonth} data-id="element-2010">
                        <ChevronDownIcon size={14} className="transform -rotate-90" data-id="element-2011" />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-gray-400 mb-1" data-id="element-2012">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <div key={d} data-id="element-2013">{d}</div>)}
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center" data-id="element-2014">
                      {calendar.weeks.flatMap(week => week.map((date, i) => date ? <button key={i} type="button" disabled={isPast(date)} className={`w-8 h-8 rounded-full flex items-center justify-center text-xs transition-colors ${isSelected(date) ? 'bg-[#F7941D] text-white' : isToday(date) ? 'border border-[#F7941D] text-[#F7941D]' : isPast(date) ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}`} onClick={() => handleDateSelect(date)} data-id="element-2015">
                              {date.getDate()}
                            </button> : <div key={i} className="w-8 h-8" data-id="element-2016" />))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2" data-id="element-2017">
                      {quickDates.map(q => <button key={q.days} type="button" className="flex-1 text-xs font-medium text-[#F7941D] hover:bg-[#F7941D]/5 py-1.5 rounded-md transition-colors" onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() + q.days);
                onExpiryDateChange(formatDate(d));
                setShowDatePicker(false);
              }} data-id="element-2018">
                          {q.label}
                        </button>)}
                    </div>
                  </div>}
              </div>
            </div>}

          {/* ── Payment Methods ── */}
          {onSelectedPaymentProvidersChange && <div className="border-t border-gray-200 px-6 py-5" data-id="element-2019">
              <div className="flex items-start justify-between" data-id="element-2020">
                <div data-id="element-2021">
                  <h4 className="text-sm font-medium text-gray-900" data-id="element-2022">
                    Limit payment methods
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-2023">
                    Restrict which payment methods are available. Leave empty to
                    show all.
                  </p>
                </div>
                {selectedPaymentProviders.length > 0 && <button type="button" className="text-xs text-[#F7941D] hover:text-[#D97B06] font-medium flex-shrink-0 ml-4 mt-0.5" onClick={() => onSelectedPaymentProvidersChange([])} data-id="element-2024">
                    Clear
                  </button>}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3" data-id="element-2025">
                {paymentProviders.map(provider => <label key={provider.id} className={`flex items-center gap-2.5 p-2.5 border rounded-lg cursor-pointer transition-colors text-sm ${selectedPaymentProviders.includes(provider.id) ? 'border-[#F7941D] bg-[#F7941D]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2026">
                    <input type="checkbox" className="h-3.5 w-3.5 text-[#F7941D] focus:ring-[#F7941D] border-gray-300 rounded" checked={selectedPaymentProviders.includes(provider.id)} onChange={() => togglePaymentProvider(provider.id)} data-id="element-2027" />
                    <span className="flex-shrink-0" data-id="element-2028">{provider.icon}</span>
                    <span className="text-xs font-medium text-gray-700 truncate" data-id="element-2029">
                      {provider.name}
                    </span>
                  </label>)}
              </div>
              {selectedPaymentProviders.length > 0 && <p className="mt-2 text-[11px] text-gray-500 flex items-center" data-id="element-2030">
                  <InfoIcon size={11} className="mr-1 text-[#F7941D] flex-shrink-0" data-id="element-2031" />
                  Only selected methods will be shown to the customer
                </p>}
            </div>}

          {/* ── Redirect URL ── */}
          {!hideRedirect && <div className="border-t border-gray-200 px-6 py-5" data-id="element-2032">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-2033">
                Success redirect URL
              </h4>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-2034">
                Where to redirect customers after a successful payment. Leave
                blank for default.
              </p>
              <div className="relative mt-3" data-id="element-2035">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2036">
                  <ExternalLinkIcon size={14} className="text-gray-400" data-id="element-2037" />
                </div>
                <input type="url" className={`${inputClass} pl-9`} placeholder="https://example.com/thank-you" value={redirectUrl} onChange={e => onRedirectUrlChange(e.target.value)} data-id="element-2038" />
              </div>
            </div>}

          {/* ── Metadata ── */}
          <div className="border-t border-gray-200 px-6 py-5" data-id="element-2039">
            <MetadataEditor metadata={metadata} onChange={onMetadataChange} data-id="element-2040" />
          </div>
        </div>}
    </div>;
}